import React, { useState } from 'react';
import type { View } from '../../types';
import { PlusIcon, ImageIcon } from '../icons';
import { useStrategicPillars, useStrategicPillarStats, useDeleteStrategicPillar, useUpdateStrategicPillarStatus } from '../../lib/api/hooks/strategic-pillar';
import { showToast } from '../../lib/toast';
import { Tab, Button, ActionDropdown, DataTable, FilterModal, type TabItem, type DropdownAction, type FilterGroup } from '../ui';
import type { StrategicPillar as StrategicPillarType, StrategicPillarFilterParams } from '../../lib/api/types';
import { useDebounce } from '../../hooks/helper-function';


interface StrategicPillarsProps {
  setView: (view: View) => void;
}

const StrategicPillars: React.FC<StrategicPillarsProps> = ({ setView }) => {
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [page] = useState(1);
  const [pageSize] = useState(50);
  const [searchValue, setSearchValue] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState<{ id: string; title: string } | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatusUpdate, setSelectedStatusUpdate] = useState<{ id: string; title: string; currentStatus: 'DRAFT' | 'PUBLISH'; newStatus: 'DRAFT' | 'PUBLISH' } | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<StrategicPillarFilterParams>({});
  const debouncedSearch = useDebounce(searchValue, 3000)

  // Filter groups configuration
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    {
      title: 'Display Options',
      key: 'display',
      type: 'checkbox',
      options: [
        { label: 'Landing Page Priorities', value: 'landingPagePrioritiesOfAfsa', checked: false },
        { label: 'Who We Are', value: 'whoWeAre', checked: false },
        { label: 'Countries', value: 'countries', checked: false },
        { label: 'History', value: 'history', checked: false },
        { label: 'Member', value: 'member', checked: false }
      ]
    }
  ]);


  const { data: statsData } = useStrategicPillarStats();
  const stats = statsData || { draft: 0, published: 0, total: 0 };


  const { data: pillarsData, isLoading, error } = useStrategicPillars(
    { page, size: pageSize },
    {
      status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT',
      ...(debouncedSearch && { title: debouncedSearch }),
      ...activeFilters
     }
  );

  // Handle filter option toggle
  const handleFilterChange = (groupKey: string, optionValue: string | boolean) => {
    setFilterGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.key === groupKey
          ? {
              ...group,
              options: group.options.map((opt) =>
                opt.value === optionValue
                  ? { ...opt, checked: group.type === 'radio' ? true : !opt.checked }
                  : group.type === 'radio'
                  ? { ...opt, checked: false }
                  : opt
              )
            }
          : group
      )
    );
  };

  // Apply filters
  const handleApplyFilters = (filters: Record<string, unknown>) => {
    const apiFilters: StrategicPillarFilterParams = {};

    // Map filter keys to API parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'display') {
        // Handle display options
        if (Array.isArray(value)) {
          value.forEach((v) => {
            if (typeof v === 'string') {
              (apiFilters as Record<string, boolean>)[v] = true;
            }
          });
        } else if (typeof value === 'string') {
          (apiFilters as Record<string, boolean>)[value] = true;
        }
      }
    });

    setActiveFilters(apiFilters);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilterGroups((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        options: group.options.map((opt) => ({ ...opt, checked: false }))
      }))
    );
    setActiveFilters({});
  };


  const { mutate: deletePillar, isPending: isDeleting } = useDeleteStrategicPillar();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateStrategicPillarStatus();

  const pillars = pillarsData?.data?.data || [];
  const publishedCount = stats.published;
  const draftsCount = stats.draft;


  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];


  const handleEdit = (pillarId: string) => {
    setView({ page: 'Strategic Pillars', subPage: 'edit', id: pillarId });
  };


  const handleDeleteClick = (pillar: { strategicPillarId: string; title: string }) => {
    setSelectedPillar({ id: pillar.strategicPillarId, title: pillar.title });
    setDeleteModalOpen(true);
  };

  // Handle status update click
  const handleStatusUpdateClick = (pillar: StrategicPillarType) => {
    const newStatus = pillar.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
    setSelectedStatusUpdate({
      id: pillar.strategicPillarId,
      title: pillar.title,
      currentStatus: pillar.status,
      newStatus
    });
    setStatusModalOpen(true);
  };


  const confirmDelete = () => {
    if (!selectedPillar) return;

    deletePillar(selectedPillar.id, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Strategic pillar deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedPillar(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  // Confirm status update
  const confirmStatusUpdate = () => {
    if (!selectedStatusUpdate) return;

    updateStatus(
      { strategicPillarId: selectedStatusUpdate.id, status: selectedStatusUpdate.newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              selectedStatusUpdate.newStatus === 'PUBLISH'
                ? 'Strategic pillar published successfully!'
                : 'Strategic pillar unpublished successfully!'
            );
            setStatusModalOpen(false);
            setSelectedStatusUpdate(null);
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };



  const getDropdownActions = (pillar: StrategicPillarType): DropdownAction[] => [
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit(pillar.strategicPillarId);
      }
    },
    {
      label: pillar.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
      onClick: (e) => {
        e.stopPropagation();
        handleStatusUpdateClick(pillar);
      },
      disabled: isUpdatingStatus
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        handleDeleteClick(pillar);
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];


  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };




  const DeleteModal: React.FC = () => (
    <>
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDeleteModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Delete Strategic Pillar</h3>
                <p className="text-sm text-gray-500 mt-1">Are you sure you want to delete this strategic pillar?</p>
              </div>
            </div>

            {selectedPillar && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-900">{selectedPillar.title}</p>
              </div>
            )}

            <p className="text-sm text-gray-600 mb-6">
              This action cannot be undone. The strategic pillar will be permanently deleted.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const StatusUpdateModal: React.FC = () => (
    <>
      {statusModalOpen && selectedStatusUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setStatusModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-4">
              <div className={`flex-shrink-0 w-12 h-12 ${selectedStatusUpdate.newStatus === 'PUBLISH' ? 'bg-green-100' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                <svg className={`w-6 h-6 ${selectedStatusUpdate.newStatus === 'PUBLISH' ? 'text-green-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {selectedStatusUpdate.newStatus === 'PUBLISH' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedStatusUpdate.newStatus === 'PUBLISH' ? 'Publish Strategic Pillar' : 'Unpublish Strategic Pillar'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedStatusUpdate.newStatus === 'PUBLISH'
                    ? 'Are you sure you want to publish this strategic pillar?'
                    : 'Are you sure you want to unpublish this strategic pillar?'}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-900">{selectedStatusUpdate.title}</p>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              {selectedStatusUpdate.newStatus === 'PUBLISH'
                ? 'This strategic pillar will be visible to the public once published.'
                : 'This strategic pillar will be hidden from the public and moved to drafts.'}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStatusModalOpen(false)}
                disabled={isUpdatingStatus}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusUpdate}
                disabled={isUpdatingStatus}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 ${
                  selectedStatusUpdate.newStatus === 'PUBLISH'
                    ? 'bg-[#047857] hover:bg-[#059669]'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {isUpdatingStatus
                  ? 'Updating...'
                  : selectedStatusUpdate.newStatus === 'PUBLISH' ? 'Publish' : 'Unpublish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <DeleteModal />
      <StatusUpdateModal />
      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        filterGroups={filterGroups}
        onChange={handleFilterChange}
      />
      <div className="space-y-6">

        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => setView({ page: 'Strategic Pillars', subPage: 'new' })}
          >
            Create New
          </Button>
        </div>


        <DataTable
          data={pillars}
          columns={[]} // We're using custom renderRow
          searchable
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filterable
          onFilterClick={() => setFilterModalOpen(true)}
          loading={isLoading}
          loadingMessage="Loading strategic pillars..."
          error={error}
          errorTitle="Error Loading Strategic Pillars"
          emptyTitle={activeTab === 'published' ? 'No Recent Post' : 'No Draft'}
          emptyMessage={activeTab === 'published' ? 'You are yet to create a post' : 'Your draft is empty'}
          emptyAction={
            activeTab === 'published' ? (
              <Button
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => setView({ page: 'Strategic Pillars', subPage: 'new' })}
              >
                Create New
              </Button>
            ) : undefined
          }
          renderRow={(pillar) => (
            <div
              key={pillar.strategicPillarId}
              className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 rounded-lg border border-gray-200 ${isDeleting ? 'opacity-50' : ''}`}
              onClick={() => setView({ page: 'Strategic Pillars', subPage: activeTab === 'published' ? 'detail' : 'edit', id: pillar.strategicPillarId })}
            >
              {pillar.coverImage ? (
                <img src={pillar.coverImage} alt={pillar.title} className="w-20 h-16 object-cover rounded-md flex-shrink-0 bg-gray-200/20" />
              ) : (
                <div className="w-20 h-16 bg-gray-200/20 rounded-md flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm truncate">{pillar.title}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(pillar.createdAt)} | {pillar.createdByUsername}
                </p>
              </div>
              <ActionDropdown
                id={pillar.strategicPillarId}
                actions={getDropdownActions(pillar as unknown as StrategicPillarType)}
              />
            </div>
          )}
        />
      </div>
    </>
  );
};

export default StrategicPillars;
